import { SaveOutlined } from '@mui/icons-material'
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd'
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Typography,
} from '@mui/material'
import { useAppController } from 'customHooks/useAppController'
import { useWorkingStepFilter } from 'features/BillOfMaterials/BoMItemFilter/Components/WorkingStepsFilter/useWorkingStepFilter'
import { WorkingStepsFilter } from 'features/BillOfMaterials/BoMItemFilter/Components/WorkingStepsFilter/WorkingStepsFilter'
import { useDialogManager } from 'features/DialogManager/useDialogManager'
import { ProjectSuppliersController } from 'features/ProjectSuppliers/ProjectSuppliersController'
import { BomItemType } from 'model/Project/BoMItemRow'
import { useCallback, useState } from 'react'
import {
  ProjectSupplierDto,
  WorkingStepType,
} from 'services/APIs/InternalAPI/internal-api.contracts'
import store from 'store/configureStore'
import { useShowException } from 'utils/useShowException'
import { ProjectRFQLines, RFQLine } from '../Components/ProjectRFQLines'
import { ProjectRFQController } from '../ProjectRFQController'

export type DraftRFQDialogProps = {
  isOpen: boolean
  onClose: () => void
}

const getSupplierWorkingStepTypes = (
  supplier: ProjectSupplierDto | null | undefined,
) =>
  new Set(
    (supplier?.activities ?? [])
      .map((activity) => activity.workingStepType)
      .filter(Boolean) as WorkingStepType[],
  )

const applySupplierActivityDefaults = (
  activities: RFQLine['activities'],
  supplier: ProjectSupplierDto | null | undefined,
) => {
  if (!activities) {
    return activities
  }

  if (!supplier) {
    return activities.map((activity) => ({
      ...activity,
      selected: activity.selected ?? true,
    }))
  }

  const supplierWorkingStepTypes = getSupplierWorkingStepTypes(supplier)

  return activities.map((activity) => ({
    ...activity,
    selected:
      !!activity.workingStepType &&
      supplierWorkingStepTypes.has(activity.workingStepType),
  }))
}

export const DraftRFQDialog = (props: DraftRFQDialogProps) => {
  const { controller, loading } = useAppController(
    () => new ProjectSuppliersController(),
  )

  const { controller: projectRFQController, loading: projectRFQLoading } =
    useAppController(() => new ProjectRFQController())

  const [availableSuppliers, setAvailableSuppliers] = useState<
    ProjectSupplierDto[]
  >([])

  const { workingStepFilter } = useWorkingStepFilter()

  const [draftRFQLines, setDraftRFQLines] = useState<RFQLine[]>([])

  const ShowException = useShowException('project suppliers')

  const { onClose } = props

  const handleCreateRequestForQuotation = useCallback(async () => {
    try {
      const resp =
        await projectRFQController.createRequestForQuotation(draftRFQLines)

      console.log('Created RFQ with resp', resp)

      onClose()
    } catch (err) {
      ShowException(err)
    }
  }, [ShowException, draftRFQLines, onClose, projectRFQController])

  const getSuppliersForDraftRFQ = useCallback(
    async (lines?: RFQLine[]) => {
      try {
        const linesToUse = lines ?? draftRFQLines
        const workingStepTypes = new Set(
          linesToUse
            .map((line) => line.activities.map((a) => a.workingStepType))
            .flat(),
        )

        if (workingStepTypes.size === 0) {
          setAvailableSuppliers([])
          return
        }

        const suppliers = await controller.getSuppliers(
          Array.from(workingStepTypes),
        )

        setAvailableSuppliers(suppliers.projectSuppliers ?? [])
      } catch (err) {
        ShowException(err)
      }
    },
    [controller, draftRFQLines, ShowException],
  )

  const applySelectedWorkingSteps = useCallback(() => {
    const projectState = store.getState().project

    if (!projectState) return

    const filteredAssemblyHeaders = projectState.filteredAssemblyHeaderIds

    // use filtered assemblies and parttypes to generate the draft RFQ
    const newRFQLines: RFQLine[] = []

    for (const assemblyId of filteredAssemblyHeaders) {
      if (
        projectState.assemblyHeaders[assemblyId].assemblyActivities.some((x) =>
          workingStepFilter.filter.enabledFilters.includes(
            x.primaryWorkingStep,
          ),
        )
      ) {
        const existingLine = draftRFQLines.find(
          (line) => line.bomItemPointer.id === assemblyId,
        )
        if (existingLine) {
          newRFQLines.push(existingLine)
        } else {
          newRFQLines.push({
            bomItemPointer: {
              id: assemblyId,
              type: BomItemType.assemblyType,
            },
            activities: projectState.assemblyHeaders[
              assemblyId
            ].assemblyActivities.map((x) => ({
              activityId: x.id,
              workingStepType: x.primaryWorkingStep,
              selected: true,
            })),
            supplier: null,
          })
        }
      }

      const partTypes =
        projectState.assemblyHeaders[assemblyId].filteredPartTypePointers

      for (const partTypePointer of partTypes) {
        const partType = projectState.partTypes[partTypePointer.id]
        if (
          partType.activities.some((x) =>
            workingStepFilter.filter.enabledFilters.includes(
              x.primaryWorkingStep,
            ),
          )
        ) {
          const existingLine = draftRFQLines.find(
            (line) =>
              line.bomItemPointer.id === partTypePointer.id &&
              line.bomItemPointer.type === BomItemType.partType,
          )
          if (existingLine) {
            newRFQLines.push(existingLine)
            continue
          } else {
            newRFQLines.push({
              bomItemPointer: {
                id: partTypePointer.id,
                type: BomItemType.partType,
              },
              activities: partType.activities.map((x) => ({
                activityId: x.id,
                workingStepType: x.primaryWorkingStep,
                selected: true,
              })),
              supplier: null, // supplier will be determined later based on activities
            })
          }
        }
      }
    }

    setDraftRFQLines(newRFQLines)
    getSuppliersForDraftRFQ(newRFQLines)
  }, [
    draftRFQLines,
    getSuppliersForDraftRFQ,
    workingStepFilter.filter.enabledFilters,
  ])

  const [selectedBomItemIds, setSelectedBomItemIds] = useState<Set<string>>(
    new Set(),
  )

  const handleDeleteSelectedRows = useCallback(() => {
    if (selectedBomItemIds.size === 0) return

    const updatedLines = draftRFQLines.filter(
      (line) => !selectedBomItemIds.has(line.bomItemPointer.id),
    )

    setDraftRFQLines(updatedLines)
    setSelectedBomItemIds(new Set())
    getSuppliersForDraftRFQ(updatedLines)
  }, [draftRFQLines, selectedBomItemIds, getSuppliersForDraftRFQ])

  const handleBulkAssignSupplier = useCallback(
    (supplier: ProjectSupplierDto) => {
      setDraftRFQLines((prev) =>
        prev.map((line) => {
          // If items are selected, only assign to selected items
          if (
            selectedBomItemIds.size > 0 &&
            !selectedBomItemIds.has(line.bomItemPointer.id)
          ) {
            return line
          }
          return {
            ...line,
            supplier,
            activities: applySupplierActivityDefaults(
              line.activities,
              supplier,
            ),
          }
        }),
      )
    },
    [selectedBomItemIds],
  )

  const handleChangeGroupSupplier = useCallback(
    (oldSupplierId: string | null, newSupplier: ProjectSupplierDto) => {
      setDraftRFQLines((prev) =>
        prev.map((line) => {
          const currentId = line.supplier?.id ?? null
          return currentId === oldSupplierId
            ? {
                ...line,
                supplier: newSupplier,
                activities: applySupplierActivityDefaults(
                  line.activities,
                  newSupplier,
                ),
              }
            : line
        }),
      )
    },
    [],
  )

  const handleLineSupplierChange = useCallback(
    (bomItemId: string, supplier: ProjectSupplierDto | null) => {
      setDraftRFQLines((prev) =>
        prev.map((line) =>
          line.bomItemPointer.id === bomItemId
            ? {
                ...line,
                supplier,
                activities: applySupplierActivityDefaults(
                  line.activities,
                  supplier,
                ),
              }
            : line,
        ),
      )
    },
    [],
  )

  const handleLineActivitySelectionChange = useCallback(
    (bomItemId: string, activityIndex: number, selected: boolean) => {
      setDraftRFQLines((prev) =>
        prev.map((line) => {
          if (line.bomItemPointer.id !== bomItemId || !line.activities) {
            return line
          }

          return {
            ...line,
            activities: line.activities.map((activity, index) =>
              index === activityIndex ? { ...activity, selected } : activity,
            ),
          }
        }),
      )
    },
    [],
  )

  const handleUnassignAll = useCallback(() => {
    setDraftRFQLines((prev) =>
      prev.map((line) => ({ ...line, supplier: null })),
    )
  }, [])

  const supplierCount = new Set(
    draftRFQLines.filter((l) => l.supplier).map((l) => l.supplier.id),
  ).size

  const unassignedCount = draftRFQLines.filter((l) => !l.supplier).length
  const linesWithoutSelectedActivities = draftRFQLines.filter(
    (line) => !(line.activities ?? []).some((activity) => activity.selected),
  ).length
  const canSave =
    draftRFQLines.length > 0 &&
    unassignedCount === 0 &&
    linesWithoutSelectedActivities === 0

  return (
    <Dialog
      open={props.isOpen}
      onClose={props.onClose}
      fullWidth
      maxWidth="xxl"
      slotProps={{
        paper: { sx: { height: '85vh', maxHeight: '85vh' } },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Draft Request for Quotation
          </Typography>
          {draftRFQLines.length > 0 && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              {supplierCount > 0 && (
                <Chip
                  size="small"
                  color="primary"
                  label={`${supplierCount} RFQ${supplierCount > 1 ? 's' : ''}`}
                />
              )}
              <Chip
                size="small"
                variant="outlined"
                label={`${draftRFQLines.length} items`}
              />
            </Box>
          )}
        </Box>
      </DialogTitle>

      <DialogContent
        sx={{ display: 'flex', gap: 2, overflow: 'hidden', pt: 1 }}
      >
        {/* Left sidebar — Working Step Filter */}
        <Box
          sx={{
            minWidth: 220,
            maxWidth: 260,
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
            borderRight: 1,
            borderColor: 'divider',
            pr: 2,
            overflow: 'auto',
          }}
        >
          <Typography
            variant="caption"
            color="secondary"
            sx={{
              fontWeight: 600,
              textTransform: 'uppercase',
            }}
          >
            Filter by working steps
          </Typography>
          <WorkingStepsFilter />
          <Button
            variant="contained"
            size="small"
            startIcon={<PlaylistAddIcon />}
            onClick={applySelectedWorkingSteps}
            sx={{ mt: 1 }}
          >
            Apply & load items
          </Button>
        </Box>

        {/* Right panel — RFQ Lines grouped by supplier */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          {draftRFQLines.length === 0 ? (
            <Box
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 2,
              }}
            >
              <Typography variant="subtitle1" color="secondary">
                Select working steps and click "Apply" to add RFQ lines.
              </Typography>
            </Box>
          ) : (
            <Box
              sx={{
                height: '100%',
                overflow: 'auto',
              }}
            >
              <ProjectRFQLines
                rfqLines={draftRFQLines}
                onSelectionChange={(bomItemId, checked) => {
                  setSelectedBomItemIds((prev) => {
                    const newSet = new Set(prev)
                    if (checked) {
                      newSet.add(bomItemId)
                    } else {
                      newSet.delete(bomItemId)
                    }
                    return newSet
                  })
                }}
                selectedBomItemIds={selectedBomItemIds}
                availableSuppliers={availableSuppliers}
                onBulkAssignSupplier={handleBulkAssignSupplier}
                onChangeGroupSupplier={handleChangeGroupSupplier}
                onLineSupplierChange={handleLineSupplierChange}
                onLineActivitySelectionChange={
                  handleLineActivitySelectionChange
                }
                onUnassignAll={handleUnassignAll}
                onDeleteSelected={handleDeleteSelectedRows}
                selectionCount={selectedBomItemIds.size}
              />
            </Box>
          )}
        </Box>
      </DialogContent>

      <Divider />
      <DialogActions sx={{ px: 3, py: 1.5, justifyContent: 'flex-end' }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" onClick={props.onClose}>
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={
              projectRFQLoading['createRequestForQuotation'] ? (
                <CircularProgress size={20} />
              ) : (
                <SaveOutlined />
              )
            }
            disabled={
              !canSave || projectRFQLoading['createRequestForQuotation']
            }
            onClick={handleCreateRequestForQuotation}
          >
            {supplierCount > 0
              ? `Save ${supplierCount} RFQ${supplierCount > 1 ? 's' : ''}`
              : 'Save RFQ'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  )
}

export const OpenRFQDraftDialogButton = () => {
  const { openDialog } = useDialogManager()

  const handleOpenDialog = () => {
    openDialog('DraftRFQDialog')
  }

  return <Button onClick={handleOpenDialog}>Draft RFQ</Button>
}
